import { ContactStatus } from '../clinical.model';

/**
 * CCM care plan time, S2.4 (BRD-CLIN-2026-008 FR-4): a time entry the nurse left to build a care plan
 * first. Held in memory for the open tab only — never written to the browser or the server — and
 * dropped when the nurse leaves the patient's Care Plans tab without coming back to it.
 */

/** Where the time form was opened from: what closing it does to the chart timer depends on it. */
export type ClinicalTimeOrigin = 'timer' | 'summary' | 'edit';

/** The result the time form closes with when the nurse leaves it to build a care plan. */
export const CLINICAL_TIME_LEFT_FOR_CARE_PLAN = 'left-for-care-plan';

/** One row of the time form, as typed. */
export interface PendingTimeRow {
  mins: string;
  secs: string;
  categories: Record<string, boolean>;
}

/** The choices under the rows, shared by all of them: the contact answer, or none yet, and the checkboxes. */
export interface PendingTimeFlags {
  exclude: boolean;
  contact_status: ContactStatus | null;
  require_provider_intervention: boolean;
  device_troubleshooting: boolean;
}

/** What reopening the time form restores: every row as it was and the flags under them. */
export interface ClinicalTimeRestore {
  rows: PendingTimeRow[];
  flags: PendingTimeFlags;
  /** On an edit, the row that is the stored entry, or -1 when the nurse deleted it. */
  editedRowIndex: number;
}

export interface PendingCcmTime {
  patientId: string;
  patientName: string;
  /** The data the time form was opened with, to reopen it the same way: an edit keeps its id, every entry its origin. */
  dialogData: Record<string, unknown>;
  restore: ClinicalTimeRestore;
  /** The CCM row the nurse left from, whose time the bar shows. */
  returnRowIndex: number;
  /** `'create'` opens a new care plan; `'add-goals'` when the patient has plans but no open goal. */
  mode: 'create' | 'add-goals';
}

/**
 * What the Care Plans tab tells the bar above it about the care plan the waiting entry needs: whether it
 * can be saved yet, and whether it is being saved.
 */
export interface CcmReturnPlanStatus {
  ready: boolean;
  saving: boolean;
}
